import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { AbstractControl, NgForm, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import Decimal from 'decimal.js';
import { BroadcastService, HttpService, MessageService, PermissionService } from 'src/app/core';
import { MsgConstantConfig } from 'src/app/core/config/message.constant.config';
import { PathConfig } from 'src/app/core/config/path.config';
import { Utility } from 'src/app/core/utils/utility';

@Component({
  selector: 'app-waive-fee',
  templateUrl: './waive-fee.component.html',
  styleUrls: ['./waive-fee.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  providers: [CurrencyPipe]
})
export class WaiveFeeComponent implements OnInit {

  @Input() loanDetail!: any;
  @Input() loanID!: any;
  submitted: boolean = false;

  @ViewChild('waiveFeeForm') formElement!: NgForm;
  waiveFeesForm!: UntypedFormGroup;
  isWaiveFeeExceed: boolean = false;
  balanceAfter: any = {
    late_fees: 0,
    nsf_fees: 0,
    other_fees: 0
  }

  lfees: any = 0;
  nfees: any = 0;
  ofees: any = 0;
  isLower: boolean = false;

  isDirty: boolean = false;
  errorMsg: string = '';
  waivePermistion: boolean = false;
  form!: UntypedFormGroup;
  boadCasterService: any;
  queueType: string = '';
  sortBy: string = '';
  orderBy: string = '';
  pageLimit: number = 0;
  currentPage: number = 0;
  isBeforFees: boolean = false;
  loanLateFee: any = 0;
  loanNsfFee: any = 0;
  loanOtherFee: any = 0;
  paymentAmountLimit = 100000000//100M(100,000,000.00);
  loneNegativeInputs: { [key: string]: boolean } = {};

  constructor(
    private httpService: HttpService,
    private msgService: MessageService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    public activeModal: NgbActiveModal,
    private route: Router,
    private permissionService: PermissionService,
    private broadCastService: BroadcastService,
    private translateService: TranslateService,
    private currencyPipe: CurrencyPipe
  ) {
    this.translateService.setDefaultLang('en');
    this.translateService.use(localStorage.getItem('Selected_Language') || 'en');
    this.waivePermistion = this.permissionService.isSpecialPermission('waive_fees');

    this.activatedRoute.queryParams
      .subscribe(params => {
        this.queueType = params['qtype'] == null ? "" : params['qtype'];
        this.sortBy = params['sortby'] == null ? "" : params['sortby'];
        this.orderBy = params['orderby'] == null ? "" : params['orderby'];
        this.pageLimit = params['limit'] == null ? "" : params['limit'];
        this.currentPage = params['page'] == null ? "" : params['page'];

      });
  }

  ngOnInit(): void {
    this.waiveFeesForm = new UntypedFormGroup({
      // A lone minus sign is a valid intermediate mask value; the blur handler normalizes it to 0.00.
      late_fees: new UntypedFormControl('0.00', [Validators.required, this.currencyRangeValidator.bind(this), this.balanceAfterRangeValidator('late_fees')]),
      nsf_fees: new UntypedFormControl('0.00', [Validators.required, this.currencyRangeValidator.bind(this), this.balanceAfterRangeValidator('nsf_fees')]),
      other_fees: new UntypedFormControl('0.00', [Validators.required, this.currencyRangeValidator.bind(this), this.balanceAfterRangeValidator('other_fees')]),
      reference_note: new UntypedFormControl('', [Validators.maxLength(200), Validators.pattern('^(?!\\s*$).+'), Validators.minLength(4)]),
    });
    this.waiveFeesForm.valueChanges.subscribe((e) => (this.isDirty = true));
    this.setFeeBalances();

    //form controls
    this.form = new UntypedFormGroup({
      employee_initials: new UntypedFormControl(null, [Validators.required]),
      pin: new UntypedFormControl(null, [Validators.required]),
    });


    this.loanID = Number(this.route?.url?.split('/')?.filter(Boolean)?.slice(-2, -1)[0]);
    this.getLoanDetailByID(this.loanID);
  }

  // get loan detail
  getLoanDetailByID(id: Number) {

    const url = `${PathConfig.LOANS}/${id}?sort_by=${this.sortBy}&sort_order=${this.orderBy}&limit=${this.pageLimit}&${this.queueType}=1&page=${this.currentPage}`;

    this.httpService.get(url).subscribe((res) => {
      this.loanDetail = res;
      this.setFeeBalances();
      this.updateAfterValues();

    });
  }

  feesEntered(e: any, ftype: string) {
    const input = e?.target as HTMLInputElement | null;
    const rawValue = input?.value ?? this.waiveFeesForm.get(ftype)?.value ?? '';
    const normalizedValue = this.normalizeCurrencyInputValue(rawValue, 'fee');
    const safeFormattedValue = this.formatCurrencyInputValue(normalizedValue);

    this.loneNegativeInputs[ftype] = false;

    if (input && input.value !== safeFormattedValue) {
      input.value = safeFormattedValue;
    }

    this.waiveFeesForm.get(ftype)?.setValue(safeFormattedValue, { emitEvent: false });
    this.updateAfterValues();
  }

  onFeeInput(event: Event, fieldName: string) {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value ?? '';
    const normalizedValue = this.normalizeCurrencyInputValue(rawValue, 'fee');

    this.loneNegativeInputs[fieldName] = normalizedValue === '-';

    if (input.value !== normalizedValue) {
      input.value = normalizedValue;
    }

    this.waiveFeesForm.get(fieldName)?.setValue(normalizedValue, { emitEvent: false });
    this.updateFeeBalance(fieldName, this.getBalanceBefore(fieldName), false);
  }

  isLoneNegativeInput(fieldName: string): boolean {
    return this.loneNegativeInputs[fieldName] === true;
  }

  updateAfterValues() {
    this.updateFeeBalance('late_fees', this.loanLateFee);
    this.updateFeeBalance('nsf_fees', this.loanNsfFee);
    this.updateFeeBalance('other_fees', this.loanOtherFee);
  }

  private setFeeBalances() {
    this.loanLateFee = this.toNumber(this.loanDetail?.account_late_fees_balance);
    this.loanNsfFee = this.toNumber(this.loanDetail?.account_nsf_fees_balance);
    this.loanOtherFee = this.toNumber(this.loanDetail?.account_other_fees_balance);
  }

  private updateFeeBalance(fieldName: string, balanceBefore: number, normalizeValue: boolean = true) {
    const control = this.waiveFeesForm.get(fieldName);
    const enteredAdjustment = this.getNumericInputValue(control?.value);
    const adjustment = enteredAdjustment !== null ? enteredAdjustment : 0;

    if (control?.value != null && control.value !== '') {
      this.balanceAfter[fieldName] = Number(new Decimal(balanceBefore).plus(adjustment).toFixed(2));
    }

    if (normalizeValue && control?.value === '-') {
      control.setValue(this.checkForNan(adjustment), { emitEvent: false });
    }

    control?.updateValueAndValidity({ emitEvent: false });
  }

  private toNumber(value: any): number {
    const parsedValue = Number(String(value ?? 0).replace(/,/g, ''));
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private normalizeCurrencyInputValue(value: string, inputType: 'fee' | 'amount' = 'fee'): string {
    const rawValue = String(value ?? '').trim();

    if (!rawValue || rawValue === '-') {
      return rawValue;
    }

    const sanitizedValue = rawValue.replace(/[^0-9,.-]/g, '');
    if (!sanitizedValue) {
      return '';
    }

    const hasLeadingMinus = sanitizedValue.startsWith('-');
    const withoutSign = hasLeadingMinus ? sanitizedValue.slice(1) : sanitizedValue;
    const [integerPartRaw = '', ...decimalParts] = withoutSign.split('.');
    const integerDigits = integerPartRaw.replace(/,/g, '').slice(0, 5);
    const decimalDigits = decimalParts.join('.').replace(/,/g, '').slice(0, 2);
    const hasDecimal = sanitizedValue.includes('.');

    if (!hasDecimal) {
      return `${hasLeadingMinus ? '-' : ''}${integerDigits}`;
    }

    return `${hasLeadingMinus ? '-' : ''}${integerDigits}${decimalDigits ? `.${decimalDigits}` : '.'}`;
  }

  private formatCurrencyInputValue(value: string): string {
    const numericValue = this.getNumericInputValue(value);
    if (numericValue === null) {
      return '0.00';
    }

    return this.checkForNan(numericValue);
  }

  onWaiveSubmit() {
    this.formElement.ngSubmit.emit();
    (this.formElement as any).submitted = true;
    this.onSubmit();
  }

  verifyPin() {
    if (this.form.valid) {
      const params = this.form.getRawValue();
      const url = `${PathConfig.VERIFY_PIN}`;
      this.httpService.post(url, params).subscribe({
        next: (res) => {

          if (res?.message != 'User not found' && res?.message != 'User validation failed') {
            this.msgService.showSuccess(res?.message);
            this.waivePermistion = true;
          }
          else {
            this.msgService.showError(res?.message);
          }
          this.submitted = false;
          this.isDirty = false;
          this.form.reset();

        },
        error: (err) => {
          const msgList = Utility.getErrorMessage(err.error);
          msgList.forEach((msg) => this.msgService.showError(msg));
        }
      });

    }

  }

  openOverrideModal(content: any) {
    this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'sm',
      windowClass: 'draggable-container',
      backdrop: false
    });
  }

  onSubmit() {
    if (this.waiveFeesForm.valid && !this.submitted) {
      this.submitted = true;
      const paramsWaiveFee = this.waiveFeesForm.value;
      paramsWaiveFee.loan_id = this.loanID;
      const url = `${PathConfig.WAIVE_OFF_FEE}/${this.loanID}`;
      this.httpService.post(url, paramsWaiveFee).subscribe((response: any) => {

        this.msgService.showSuccess(response?.message);
        this.submitted = false;
        this.isDirty = false;
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_QUEUE_DETAIL_DATA);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_LOAN_TRANSACTION_LIST);
        this.broadCastService.broadcast(MsgConstantConfig.REFRESH_ACCOUNT_HISTORY_LIST);
        this.activeModal.dismiss('Cross click');
      },
        (err) => {
          const msgList = Utility.getErrorMessage(err.error);
          msgList.forEach((msg) => this.msgService.showError(msg));
          this.submitted = false;
          this.errorMsg = 'Unable to waive fees, something went wrong';
          this.isWaiveFeeExceed = true;
        });
    }
  }

  currencyRangeValidator(control: AbstractControl) {
    const value = this.getNumericInputValue(control.value);
    if (value === null) {
      return null;
    }

    const isValid = value >= -10000 && value <= 10000;
    return isValid ? null : { currencyRange: true };
  }

  private getNumericInputValue(value: any): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const normalizedValue = String(value ?? '').replace(/[^0-9.-]+/g, '').trim();
    if (!normalizedValue || normalizedValue === '-') {
      return null;
    }

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  checkForNan(num: any) {
    if (!isNaN(num) && num != '') {
      return this.roundToDecimal((num), 2);
    }
    else {
      return parseFloat('0.00')?.toFixed(2);
    }
  }

  roundToDecimal(num: any, length: any) {
    return (new Decimal((num))).toFixed((length) || 0);
  }

  balanceAfterRangeValidator(fieldName: string) {
    return (control: AbstractControl) => {
      const balanceBefore = this.getBalanceBefore(fieldName);
      const adjustment = this.toNumber(control.value);
      const value = balanceBefore + adjustment;
      const min = 0;
      const max = this.paymentAmountLimit;
      const isValid = value >= min && value <= max;
      return isValid ? null : {
        invalidBalanceAfterRange: {
          message: `${this.translateService.instant('Balance After must be between')} ${this.currencyPipe.transform(min, 'USD', 'symbol', '1.2-2')} ${this.translateService.instant('and')} ${this.currencyPipe.transform(max, 'USD', 'symbol', '1.2-2')}`
        }
      };
    };
  }

  private getBalanceBefore(fieldName: string): number {
    const balances: { [key: string]: number } = {
      late_fees: this.loanLateFee,
      nsf_fees: this.loanNsfFee,
      other_fees: this.loanOtherFee
    };
    return balances[fieldName] ?? 0;
  }

}
